import React, {
  useState,
  type ChangeEvent,
  type KeyboardEvent,
  type ClipboardEvent,
  type Ref,
} from "react";
import { useTranslation } from "react-i18next";
import ClassNames from "classnames";
import type { FieldError } from "react-hook-form";
import "./TextareaAutoSize.scss";
import TextareaAutosize from "react-textarea-autosize";
import SeemMarkdown from "../../../Components/SeemMarkdown/SeemMarkdown";
import {
  Tooltip,
  FormFieldError,
  SeemAI,
  formatNumber,
} from "@SeemplicityDev/shared";

interface TextareaAutoSizeAiProps {
  loading?: boolean;
  isGeneratedByAi?: boolean;
  onChangeAiValue?: () => void;
  [key: string]: unknown;
}

interface TextareaAutoSizeProps {
  "data-testid": string;
  wrapperClassName?: string;
  className?: string;
  value?: string;
  onChange?: (e: ChangeEvent<HTMLTextAreaElement>) => void;
  onKeyDown?: (e: KeyboardEvent<HTMLTextAreaElement>) => void;
  onPaste?: (e: ClipboardEvent<HTMLTextAreaElement>) => void;
  onBlur?: () => void;
  onFocus?: () => void;
  name?: string;
  error?: FieldError;
  label?: string;
  labelTop?: boolean;
  size?: string;
  required?: boolean;
  hideLabelHelper?: boolean;
  disabled?: boolean;
  readOnly?: boolean;
  labelWidth?: boolean;
  isLarge?: boolean;
  placeholder?: string;
  title?: string;
  withHelp?: string | boolean;
  rowNumber?: number;
  minRows?: number;
  maxRows?: number;
  resize?: string;
  hasAISupport?: boolean;
  applyAutoFocus?: boolean;
  aiProps?: TextareaAutoSizeAiProps;
  inputRef?: Ref<HTMLTextAreaElement>;
  maxLength?: number;
  "aria-describedby"?: string;
}

const TextareaAutoSize: React.FC<TextareaAutoSizeProps> = (props) => {
  const { t } = useTranslation();

  const {
    value,
    onChange = () => {},
    onKeyDown,
    name,
    error,
    wrapperClassName,
    label,
    labelTop = false,
    size = "max",
    required = false,
    hideLabelHelper = false,
    disabled = false,
    readOnly = false,
    labelWidth = false,
    isLarge,
    placeholder = "",
    onBlur = () => {},
    onPaste,
    onFocus = () => {},
    title,
    "data-testid": dataTestId,
    withHelp = false,
    rowNumber = 2,
    minRows,
    maxRows = 100,
    resize = "none",
    hasAISupport,
    applyAutoFocus,
    aiProps = {},
    inputRef,
    maxLength,
  } = props;

  const [isFocused, setIsFocused] = useState(false);
  // hasAISupport is optional (boolean | undefined); type the state as boolean
  // to match SeemAI's setter prop without changing the runtime initial value.
  const [previewMode, setPreviewMode] = useState<boolean>(hasAISupport!);

  const classNameWrapper = ClassNames(
    "input-wrap",
    "input-wrap-text-area",
    "auto-size-textarea",
    wrapperClassName && wrapperClassName,
    {
      "has-label": label !== null,
      "has-label-top": labelTop,
      "hide-label-helper": hideLabelHelper,
      "is-disabled": disabled,
      "has-read-only": readOnly,
      "has-size-xxs": size === "xxs",
      "has-size-xs": size === "xs",
      "has-size-s": size === "s",
      "has-size-m": size === "m",
      "has-size-l": size === "l",
      "has-size-xl": size === "xl",
      "has-size-xxl": size === "xxl",
      "has-size-max": size === "max",
      "has-label-width": labelWidth,
      "has-size-custom": size === "custom",
      "is-large": isLarge,
      "has-error": !!error,
      "with-help": withHelp,
      "has-resize": resize,
      "ai-loading": aiProps?.loading,
      "ai-generated": aiProps?.isGeneratedByAi,
    },
    props.className
  );

  const onFocusChange = () => {
    if (isFocused) {
      if (onBlur) {
        onBlur();
      }
    } else {
      if (onFocus) {
        onFocus();
      }
    }
    setIsFocused(!isFocused);
  };

  return (
    <div className={classNameWrapper}>
      {label && (
        <label className={`input-label`}>
          {t(label)}
          {!required && !hideLabelHelper && (
            <span className={"label-helper"}>Optional</span>
          )}
          {withHelp && (
            <>
              <Tooltip type="info" content={withHelp}>
                <i className={`seem-icon seem-icon-help`} />
              </Tooltip>
            </>
          )}
        </label>
      )}

      {hasAISupport && (
        <SeemAI
          {...aiProps}
          previewMode={previewMode}
          setPreviewMode={setPreviewMode}
        />
      )}
      <div className={`input-holder ${isFocused ? "is-focused" : ""}`}>
        <>
          {previewMode ? (
            <div
              className={"preview-holder"}
              onClick={() => setPreviewMode(false)}
            >
              <SeemMarkdown>{value}</SeemMarkdown>
            </div>
          ) : (
            <TextareaAutosize
              ref={inputRef}
              title={title}
              data-testid={`${dataTestId}-input`}
              disabled={disabled}
              readOnly={readOnly}
              placeholder={placeholder}
              name={name}
              rows={rowNumber}
              minRows={minRows}
              maxRows={maxRows}
              value={value || ""}
              onChange={(e) => {
                onChange(e);
                aiProps?.onChangeAiValue?.();
              }}
              onKeyDown={onKeyDown}
              onPaste={onPaste}
              onBlur={onFocusChange}
              onFocus={onFocusChange}
              autoFocus={hasAISupport || applyAutoFocus}
              maxLength={maxLength}
              // `resize` is a non-standard prop forwarded to the DOM textarea by
              // the original JS; it is not part of TextareaAutosizeProps, so it
              // is spread via a cast to preserve the rendered DOM attribute.
              {...({ resize } as Record<string, unknown>)}
            />
          )}
        </>
      </div>
      {maxLength && isFocused && (
        <div className={"char-counter"}>
          {formatNumber((value || "").length)} / {formatNumber(maxLength)}
        </div>
      )}
      {error && (
        <FormFieldError
          errorMessage={error.message}
          errorType={error.type}
          isOpen={!isFocused}
          fieldName={dataTestId}
        />
      )}
    </div>
  );
};

export default TextareaAutoSize;
